/* RESPONSIBLE TEAM: team-inbox */

import type Session from 'embercom/services/session';
import { buildCycleOrder, resolveCurrentSelected, normalizeLocale } from './language-selection';

export type FfOffOrderMode = 'translation-first' | 'original-first';

function buildTwoStateOrder(params: {
  available: string[];
  original?: string;
  mode: FfOffOrderMode;
}): string[] {
  let { available, original, mode } = params;
  let order: string[] = [];

  let normOriginal = normalizeLocale(original);
  let firstTranslated = available[0];
  let normFirst = normalizeLocale(firstTranslated);

  if (mode === 'translation-first') {
    if (normFirst) {
      order.push(normFirst);
    }
    if (normOriginal && normOriginal !== normFirst) {
      order.push(normOriginal);
    }
  } else {
    // original-first
    if (normOriginal) {
      order.push(normOriginal);
    }
    if (normFirst && normFirst !== normOriginal) {
      order.push(normFirst);
    }
  }

  return order;
}

export function computeTranslationUi(params: {
  session: Session;
  available: string[];
  preferred?: string;
  defaultLang?: string;
  original?: string;
  selectedLangCode?: string;
  ffOffOrderMode: FfOffOrderMode;
}): {
  isMultiLangTranslationsEnabled: boolean;
  cycleOrderCodes: string[];
  currentSelectedCode: string | undefined;
} {
  let { session, available, preferred, defaultLang, original, selectedLangCode, ffOffOrderMode } =
    params;

  // Safely check the feature flag; default to false if session/workspace not ready
  let isMulti =
    session.workspace?.isFeatureEnabled('hsg-inbox-multi-language-translations') ?? false;

  let order = isMulti
    ? buildCycleOrder({ available, preferred, defaultLang, original })
    : buildTwoStateOrder({ available, original, mode: ffOffOrderMode });

  // Special case: if preferred equals original, ensure the original language is first
  // This covers cases where the preferred code is not in `available` because it's the original
  if (isMulti) {
    let normPreferred = normalizeLocale(preferred);
    let normOriginal = normalizeLocale(original);
    if (normPreferred && normOriginal && normPreferred === normOriginal) {
      let idx = order.indexOf(normOriginal);
      if (idx > 0) {
        order.splice(idx, 1);
        order.unshift(normOriginal);
      } else if (idx === -1) {
        order.unshift(normOriginal);
      }
      // de-dup just in case
      order = order.filter((code, i) => order.indexOf(code) === i);
    }
  }

  let current = resolveCurrentSelected(selectedLangCode, order);

  return {
    isMultiLangTranslationsEnabled: isMulti,
    cycleOrderCodes: order,
    currentSelectedCode: current,
  };
}
